import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Box, Typography, Stack, Chip, TextField, Button, MenuItem, Divider,
    CircularProgress, IconButton, Collapse, Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBackOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlined";
import SmartToyIcon from "@mui/icons-material/SmartToyOutlined";
import BuildIcon from "@mui/icons-material/BuildOutlined";
import TuneIcon from "@mui/icons-material/TuneOutlined";
import { toast } from "react-toastify";
import api, { apiError, tokens } from "../api";
import AdminHeader, { adminPage } from "../components/AdminHeader";
import AppFooter from "../components/AppFooter";

/*
 * What the assistant said at every licensed site, and everything that happened because of
 * one question.
 *
 * Three steps: licences, that plant's conversations, then the exchange itself. The thread
 * is the unit worth reading - an answer that looks wrong alone is often right given what
 * was asked two turns earlier, and one that looks fine is sometimes the third attempt at
 * being understood.
 *
 * The exchange shows the whole path, not just the answer, because a wrong answer is almost
 * never the model being stupid. It is the wrong time window resolved before the model was
 * called, or the right tool called with the wrong tag, or a tool that failed and left it
 * guessing. From the outside all three look identical - a confident answer with the wrong
 * number in it - and each needs a different fix.
 *
 * Staff only, and behind the 'admin' role: this is the customer's own plant data.
 */

const fmt = (d) => (d ? new Date(d).toLocaleString("en-IN", { hour12: false }) : "—");

const darkField = {
    "& .MuiOutlinedInput-root": {
        color: "#fff", borderRadius: 2,
        bgcolor: "rgba(255,255,255,.04)",
        "& fieldset": { borderColor: "rgba(255,255,255,.16)" },
        "&:hover fieldset": { borderColor: "rgba(255,255,255,.32)" },
        "&.Mui-focused fieldset": { borderColor: "#7BD95F" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,.5)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#7BD95F" },
    "& .MuiSelect-icon": { color: "rgba(255,255,255,.5)" },
};

const card = {
    borderRadius: 1.5, overflow: "hidden", overflowX: "auto",
    bgcolor: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)",
};
const dim = "rgba(255,255,255,.55)";

/** A block of machine detail, folded away until it is wanted. */
function Detail({ label, icon, tone, children }) {
    const [open, setOpen] = useState(false);
    return (
        <Box sx={{ mb: .75 }}>
            <Stack direction="row" spacing={.75} onClick={() => setOpen(!open)}
                sx={{ alignItems: "center", cursor: "pointer", py: .25 }}>
                {icon}
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tone || dim }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,.35)" }}>
                    {open ? "▾" : "▸"}
                </Typography>
            </Stack>
            <Collapse in={open}>
                <Box sx={{ pl: 2.5, pb: .5 }}>{children}</Box>
            </Collapse>
        </Box>
    );
}

const Pre = ({ children, faded }) => (
    <Typography component="pre" sx={{
        m: 0, whiteSpace: "pre-wrap", wordBreak: "break-word",
        fontSize: 11, fontFamily: "monospace", maxHeight: 220, overflow: "auto",
        bgcolor: "rgba(0,0,0,.35)", color: faded ? "rgba(255,255,255,.55)" : "rgba(255,255,255,.8)",
        p: 1, borderRadius: 1, border: "1px solid rgba(255,255,255,.08)",
    }}>
        {children}
    </Typography>
);

/** One question, from what the server resolved to what the person finally read. */
function Exchange({ item, licenceId, onPrompt }) {
    const turns = item.turns || [];
    const seed = item.preSeed;
    const offer = item.offer;

    return (
        <Box sx={{ mb: 4 }}>
            {/* ---- asked ---- */}
            <Stack direction="row" spacing={1}
                sx={{ alignItems: "flex-start", justifyContent: "flex-end", mb: 1.25 }}>
                <Box sx={{ maxWidth: "72%", minWidth: 0 }}>
                    <Box sx={{
                        px: 1.75, py: 1.25, bgcolor: "#2E7D20", color: "#fff",
                        borderRadius: "12px 12px 2px 12px",
                    }}>
                        <Typography sx={{ fontSize: 14.5, whiteSpace: "pre-wrap" }}>
                            {item.queryText}
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,.45)", textAlign: "right", pr: .5 }}>
                        {fmt(item.askedAt)}{item.userId ? ` · ${item.userId}` : ""}
                    </Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 17, color: "rgba(255,255,255,.35)", mt: 1 }} />
            </Stack>

            {/* ---- what happened, in the order it happened ---- */}
            <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
                <SmartToyIcon sx={{ fontSize: 17, color: "rgba(255,255,255,.35)", mt: 1 }} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{
                        p: 1.75, borderRadius: "12px 12px 12px 2px",
                        bgcolor: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)",
                    }}>
                        {/* what the model was given to work with */}
                        {offer?.names?.length > 0 && (
                            <Detail
                                icon={<BuildIcon sx={{ fontSize: 14, color: dim }} />}
                                label={`Tools sent — ${offer.names.length}`
                                    + (offer.door ? ` · ${offer.door} door` : "")
                                    + (offer.allowActions ? " · actions on" : "")}>
                                {/* The names, not a count. When an answer says "I cannot do
                                    that", a count leaves you unable to tell whether the tool
                                    was withheld or was there and went unused. */}
                                <Stack direction="row" spacing={.5} sx={{ flexWrap: "wrap", gap: .5 }}>
                                    {offer.names.map((n) => (
                                        <Chip key={n} size="small" label={n} sx={{
                                            fontSize: 11, height: 20,
                                            bgcolor: "rgba(255,255,255,.07)", color: "rgba(255,255,255,.75)",
                                        }} />
                                    ))}
                                </Stack>
                            </Detail>
                        )}

                        {/* before the model saw anything */}
                        {seed && (seed.timeSpanMatched || seed.candidateTags?.length || seed.contextBlock) && (
                            <Detail
                                icon={<TuneIcon sx={{ fontSize: 14, color: dim }} />}
                                label={`Before the model — ${seed.hit ? "matched" : "no match"}`
                                    + (seed.timeSpanMatched ? ` · ${seed.timeSpanMatched}` : "")}>
                                {/* First step of every question and the one most often to
                                    blame: "kal ka average" resolved to the wrong window
                                    produces perfect reasoning about the wrong day. */}
                                {seed.timeSpanMatched && (
                                    <Typography sx={{ fontSize: 12, color: dim }}>
                                        time window: <b style={{ color: "#fff" }}>{seed.timeSpanMatched}</b>
                                    </Typography>
                                )}
                                {!!seed.candidateTags?.length && (
                                    <Typography sx={{ fontSize: 12, color: dim, mb: .5 }}>
                                        tags offered: {seed.candidateTags.join(", ")}
                                    </Typography>
                                )}
                                {seed.contextBlock && <Pre faded>{seed.contextBlock}</Pre>}
                            </Detail>
                        )}

                        {/* each round trip to the model */}
                        {turns.map((turn, i) => (
                            <Box key={i} sx={{ mb: 1 }}>
                                <Detail
                                    icon={<SmartToyIcon sx={{ fontSize: 14, color: dim }} />}
                                    label={`Turn ${turn.turnIdx ?? i}`
                                        + (turn.request?.messageCount ? ` — ${turn.request.messageCount} messages sent` : "")
                                        + (turn.response?.stopReason ? ` · ${turn.response.stopReason}` : "")}>
                                    <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", rowGap: .5 }}>
                                        {turn.request?.model && (
                                            <Typography sx={{ fontSize: 12, color: dim }}>
                                                model {turn.request.model}
                                                {turn.request.effort ? ` · ${turn.request.effort}` : ""}
                                            </Typography>
                                        )}
                                        {!!turn.request?.inputTokens && (
                                            <Typography sx={{ fontSize: 12, color: dim }}>
                                                in {turn.request.inputTokens.toLocaleString("en-IN")} tokens
                                            </Typography>
                                        )}
                                        {/* Why there is another turn after this one. */}
                                        {!!turn.response?.toolUseBlocks && (
                                            <Typography sx={{ fontSize: 12, color: dim }}>
                                                asked for {turn.response.toolUseBlocks} tool call
                                                {turn.response.toolUseBlocks === 1 ? "" : "s"}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Detail>

                                {(turn.toolCalls || []).map((call, j) => (
                                    <Box key={j} sx={{ pl: 1.5 }}>
                                        <Detail
                                            icon={<BuildIcon sx={{ fontSize: 14, color: call.isError ? "#FF9B9B" : dim }} />}
                                            tone={call.isError ? "#FF9B9B" : undefined}
                                            label={`${call.name}${call.isError ? " — failed" : ""}`
                                                + (call.latencyMs ? ` · ${call.latencyMs}ms` : "")}>
                                            {/* Input first, always. When an answer names the
                                                wrong machine or the wrong day, the mistake is
                                                nearly always visible right here - the tool did
                                                exactly what it was asked. */}
                                            <Typography sx={{ fontSize: 11.5, color: dim }}>sent</Typography>
                                            <Pre>{JSON.stringify(call.input ?? {}, null, 1)}</Pre>
                                            <Typography sx={{ fontSize: 11.5, color: dim, mt: .5 }}>
                                                {call.isError ? "failed with" : "came back"}
                                                {call.outputBytes ? ` (${(call.outputBytes / 1024).toFixed(1)} KB)` : ""}
                                            </Typography>
                                            <Pre faded>{call.error || call.outputPreview || "(empty)"}</Pre>
                                        </Detail>
                                    </Box>
                                ))}
                            </Box>
                        ))}

                        {(!!turns.length || seed) && (
                            <Divider sx={{ my: 1.25, borderColor: "rgba(255,255,255,.1)" }} />
                        )}

                        {/* ---- what the person read ---- */}
                        <Typography sx={{ fontSize: 14.5, whiteSpace: "pre-wrap", color: "rgba(255,255,255,.88)" }}>
                            {item.finalText || <em>no answer was produced</em>}
                        </Typography>

                        {item.error && (
                            <Typography sx={{ fontSize: 12.5, color: "#FF9B9B", mt: 1 }}>
                                failed: {item.error}
                            </Typography>
                        )}

                        <Stack direction="row" spacing={1}
                            sx={{ alignItems: "center", justifyContent: "flex-end", mt: 1, flexWrap: "wrap" }}>
                            <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,.4)" }}>
                                {item.model}
                                {` · ${Math.round((item.totalLatencyMs || 0) / 100) / 10}s`}
                                {item.totalUsage?.output_tokens ? ` · ${item.totalUsage.output_tokens} out` : ""}
                            </Typography>
                            {/* The instructions and tools behind this answer, fetched only when
                                asked for: the same twenty-odd kilobytes sit behind every question
                                in the thread, and most reviews never open them. */}
                            {item.systemPromptVersion && (
                                <Typography component="span"
                                    onClick={() => onPrompt(licenceId, item)}
                                    sx={{ fontSize: 11.5, color: "#9FE08C", cursor: "pointer" }}>
                                    prompt {item.systemPromptVersion}
                                    {item.offer?.offeredCount ? ` · ${item.offer.offeredCount} tools` : ""}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                </Box>
            </Stack>
        </Box>
    );
}

export default function AdminChatHistory() {
    const navigate = useNavigate();
    const [params] = useSearchParams();

    /*
     * One licence, named in the URL - this page is always opened from a token row, never
     * browsed. The list of every licence that used to live here was a menu with one item
     * on it for most installations, and a second click on the way to the only thing
     * anybody comes here for.
     */
    const licenceId = params.get("licence");

    const [meta, setMeta] = useState(null);           // email and plan of this licence
    const [threads, setThreads] = useState(null);
    const [people, setPeople] = useState([]);
    const [user, setUser] = useState("");

    const [prompt, setPrompt] = useState(null);       // { version, item, reason }
    const [active, setActive] = useState(null);       // the open thread
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(true);

    const fail = useCallback((e) => {
        if (e?.response?.status === 401) { tokens.console.clear(); navigate("/admin"); }
        else if (e?.response?.status === 403) toast.error("Chat history needs an admin login");
        else toast.error(apiError(e));
    }, [navigate]);

    /*
     * The instructions and tools behind one answer.
     *
     * Fetched on the click, not with the thread: the same twenty-odd kilobytes sit behind
     * every question in a conversation, and most reviews never open them.
     */
    const openPrompt = async (id, m) => {
        setPrompt({ version: m.systemPromptVersion, loading: true });
        try {
            const { data } = await api.get(`/console/chatlogs/${id}/prompt`, {
                params: { v: m.systemPromptVersion, tools: m.toolSchemaVersion || "" },
            });
            setPrompt({ version: m.systemPromptVersion, item: data.item, reason: data.reason });
        } catch (e) { fail(e); setPrompt(null); }
    };

    const openThread = useCallback(async (t) => {
        setActive(t); setItems(null);
        try {
            const { data } = await api.get(
                `/console/chatlogs/${licenceId}/threads/${encodeURIComponent(t.threadId)}`);
            setItems(data.items || []);
        } catch (e) { fail(e); setItems([]); }
    }, [licenceId, fail]);

    const load = useCallback(async (forUser = "") => {
        if (!licenceId) { setLoading(false); return; }
        setThreads(null); setActive(null); setItems(null); setUser(forUser); setLoading(true);
        try {
            const { data } = await api.get(`/console/chatlogs/${licenceId}/threads`,
                { params: forUser ? { user: forUser } : {} });
            setMeta({ email: data.email, plan: data.plan });
            setThreads(data.threads || []);
            // Only on the unfiltered load: filtering by a person must not shrink the list
            // of people you can filter by, or there is no way back to everyone.
            if (!forUser) setPeople(data.users || []);
            // Straight into the newest. The list is a way of moving between conversations,
            // not a thing to read, so landing on an empty right pane wastes a click.
            if (data.threads?.length) openThread(data.threads[0]);
        } catch (e) { fail(e); setThreads([]); }
        setLoading(false);
    }, [licenceId, fail, openThread]);

    /*
     * Opens on the admin's own conversations.
     *
     * The licence belongs to one plant and its SCADA admin is the person we are usually
     * asked about - "what did it tell Anoop?" - while the operators' questions are a
     * larger, noisier pile. Everyone is one click away in the filter, and if the admin has
     * never asked anything the filter would be empty, so that case falls back to everyone.
     */
    const started = useRef(false);
    useEffect(() => {
        if (started.current || !licenceId) { setLoading(false); return; }
        started.current = true;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/console/chatlogs/${licenceId}/threads`);
                setMeta({ email: data.email, plan: data.plan });
                setPeople(data.users || []);
                const admin = (data.users || []).find((u) => u.userId === data.email);
                if (admin) { setLoading(false); load(admin.userId); return; }
                setThreads(data.threads || []);
                if (data.threads?.length) openThread(data.threads[0]);
            } catch (e) { fail(e); setThreads([]); }
            setLoading(false);
        })();
    }, [licenceId, fail, load, openThread]);

    const promptDialog = (
        <Dialog open={Boolean(prompt)} onClose={() => setPrompt(null)} maxWidth="md" fullWidth
            slotProps={{
                paper: {
                    sx: {
                        bgcolor: "#0C1A12", color: "#fff", borderRadius: 3,
                        border: "1px solid rgba(255,255,255,.12)", backgroundImage: "none",
                    },
                },
            }}>
            <DialogTitle sx={{ fontWeight: 700 }}>
                Prompt {prompt?.version}
                {prompt?.item?.toolCount ? ` · ${prompt.item.toolCount} tools` : ""}
            </DialogTitle>
            <DialogContent>
                {prompt?.loading ? (
                    <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>
                ) : prompt?.item ? (
                    <Stack spacing={2}>
                        <Pre>{prompt.item.systemPrompt || "(no prompt text was sent)"}</Pre>
                        <Stack direction="row" spacing={.8} sx={{ flexWrap: "wrap", rowGap: .8 }}>
                            {(prompt.item.tools || []).map((t) => (
                                <Chip key={t.name} size="small" label={t.name} sx={{
                                    fontSize: 11, height: 20,
                                    bgcolor: "rgba(255,255,255,.07)", color: "rgba(255,255,255,.75)",
                                }} />
                            ))}
                        </Stack>
                    </Stack>
                ) : (
                    /* Not a failure: a trace older than this recording names a version
                       nobody ever sent up. */
                    <Typography sx={{ color: dim }}>{prompt?.reason || "Nothing stored."}</Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setPrompt(null)} sx={{ color: "rgba(255,255,255,.65)" }}>Close</Button>
            </DialogActions>
        </Dialog>
    );

    /* Nothing to show without a licence: this page is reached from a token row. */
    if (!licenceId) {
        return (
            <Box sx={{ ...adminPage, display: "flex", flexDirection: "column" }}>
                <AdminHeader />
                <Box sx={{ px: { xs: 2, md: 4 }, py: 6, textAlign: "center", color: dim }}>
                    <Typography sx={{ mb: 2 }}>
                        Open a chat history from a customer's token.
                    </Typography>
                    <Button onClick={() => navigate("/admin/accounts")}
                        sx={{ color: "#9FE08C", border: "1px solid rgba(123,217,95,.35)", borderRadius: 999, px: 3 }}>
                        Accounts
                    </Button>
                </Box>
                <AppFooter />
            </Box>
        );
    }

    /* ---------------- threads on the left, the exchange on the right ------- */
    return (
            <Box sx={{ ...adminPage, display: "flex", flexDirection: "column" }}>
                <AdminHeader />
                <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 2 }}>
                        <IconButton onClick={() => navigate(-1)}
                            sx={{
                                color: "rgba(255,255,255,.75)",
                                border: "1px solid rgba(255,255,255,.14)",
                                bgcolor: "rgba(255,255,255,.04)",
                                "&:hover": { bgcolor: "rgba(255,255,255,.09)" },
                            }}>
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ fontWeight: 800, fontSize: 20 }}>
                            AI Chat History — {meta?.email || "…"}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        {people.length > 1 && (
                            <TextField select size="small" label="Person" value={user}
                                sx={{ ...darkField, minWidth: 260 }}
                                onChange={(e) => load(e.target.value)}>
                                <MenuItem value="">Everyone</MenuItem>
                                {people.map((p) => (
                                    <MenuItem key={p.userId} value={p.userId}>
                                        {p.userId} ({p.questions})
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    </Stack>

                    {loading || threads === null ? (
                        <Box sx={{ p: 6, textAlign: "center" }}><CircularProgress size={26} /></Box>
                    ) : threads.length === 0 ? (
                        <Box sx={{ ...card, p: 6, textAlign: "center", color: dim }}>
                            Nothing has arrived from this plant.
                        </Box>
                    ) : (
                        <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                            sx={{ height: "calc(100vh - 150px)", minHeight: 0 }}>

                            <Box sx={{
                                ...card, overflowX: "hidden", overflowY: "auto",
                                width: { xs: "100%", md: 330 }, flexShrink: 0,
                                maxHeight: { xs: 220, md: "100%" },
                            }}>
                                {threads.map((t) => {
                                    const on = t.threadId === active?.threadId;
                                    return (
                                        <Box key={t.threadId} onClick={() => openThread(t)}
                                            sx={{
                                                px: 1.75, py: 1.4, cursor: "pointer",
                                                borderBottom: "1px solid rgba(255,255,255,.07)",
                                                bgcolor: on ? "rgba(46,125,32,.22)" : "transparent",
                                                "&:hover": { bgcolor: on ? "rgba(46,125,32,.22)" : "rgba(255,255,255,.05)" },
                                            }}>
                                            <Typography sx={{
                                                fontSize: 14, fontWeight: on ? 700 : 500,
                                                display: "-webkit-box", WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical", overflow: "hidden",
                                            }}>
                                                {t.title || "(no question text)"}
                                            </Typography>
                                            <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,.45)", mt: .4 }}>
                                                {fmt(t.lastAt)} · {t.messages} msg{t.user ? ` · ${t.user}` : ""}
                                            </Typography>
                                            {t.failed > 0 && (
                                                <Chip size="small" label={`${t.failed} failed`} sx={{
                                                    mt: .6, height: 19, fontSize: 11,
                                                    bgcolor: "rgba(229,62,62,.2)", color: "#FF9B9B",
                                                }} />
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>

                            <Box sx={{ ...card, flexGrow: 1, minWidth: 0, overflowY: "auto", p: { xs: 2, md: 3 } }}>
                                {items === null ? (
                                    <Box sx={{ p: 6, textAlign: "center" }}><CircularProgress size={24} /></Box>
                                ) : items.length === 0 ? (
                                    <Typography sx={{ color: dim }}>Nothing in this conversation.</Typography>
                                ) : (
                                    items.map((m) => (
                                        <Exchange key={m.traceId} item={m}
                                            licenceId={licenceId} onPrompt={openPrompt} />
                                    ))
                                )}
                            </Box>
                        </Stack>
                    )}
                </Box>
            {promptDialog}

            <AppFooter />
        </Box>
    );
}
